"use client";

import { ArrowUp } from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";

import useBeforeUnload from "@/hooks/use-before-unload";
import { useAppSelector } from "@/redux/hooks";

import FlightBulkActionsToolbar from "@/feature/flight/search/components/shared/flight-bulk-actions-toolbar";
import { NoFlightsFound } from "@/feature/flight/search/components/shared/no-flights-found";

import { FlightCardLoadingSkeleton } from "@/components/loader/flight-card-loading-skeleton";
import { IFlight } from "@/feature/flight/search/types/flight";
import CheapAirlineFilter from "../filters/cheap-airline-filter";

const VirtualFlightCard = dynamic(
  () => import("./flight-tab").then((m) => m.FlightTab),
  { ssr: false },
);

type FlightTabsProps = {
  flights: IFlight[];
};

const EMPTY_FLIGHTS: IFlight[] = [];
const LOADING_SKELETONS = Array.from({ length: 4 });

export function FlightTabs({ flights }: FlightTabsProps) {
  const resultsRootRef = useRef<HTMLDivElement | null>(null);

  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [expandedFlightId, setExpandedFlightId] = useState<string | null>(null);

  const isLoading = useAppSelector(
    (state) => state.flightSearchStatus.isLoading,
  );
  const isResponseEnd = useAppSelector(
    (state) => state.flightSearchStatus.isResponseEnd,
  );
  const error = useAppSelector((state) => state.flightSearchStatus.error);
  const selectedFlight = useAppSelector(
    (state) => state.flightData.selectFlight,
  );
  const selectedFlights = selectedFlight.data ?? EMPTY_FLIGHTS;
  const shouldShowOnlySelected = showOnlySelected && selectedFlights.length > 0;

  const sourceFlights = useMemo(
    () => (shouldShowOnlySelected ? selectedFlights : flights),
    [shouldShowOnlySelected, selectedFlights, flights],
  );

  const scrollToResultsAnchor = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!isLoading && !error && flights.length > 0) {
      requestAnimationFrame(() => {
        scrollToResultsAnchor();
      });
    }
  }, [isLoading, error, flights.length, scrollToResultsAnchor]);

  const toggleFlightExpand = useCallback((flightId: string) => {
    setExpandedFlightId((prev) => (prev === flightId ? null : flightId));
  }, []);

  const handleRangeChange = useCallback((range: { startIndex: number }) => {
    const shouldShow = range.startIndex > 4;
    setShowBackToTop((prev) => (prev === shouldShow ? prev : shouldShow));
  }, []);

  const renderFlight = useCallback(
    (index: number, flight: IFlight) => (
      <div className="pt-3 px-1 sm:px-4">
        <VirtualFlightCard
          data={flight}
          flights_index={index}
          isExpanded={expandedFlightId === flight.flight_id}
          onToggle={() => toggleFlightExpand(flight.flight_id)}
        />
      </div>
    ),
    [expandedFlightId, toggleFlightExpand],
  );

  const virtuosoComponents = useMemo(
    () => ({
      Footer: () =>
        isResponseEnd && sourceFlights.length > 0 ? (
          <div className="flex justify-center py-4 text-sm text-muted-foreground">
            No more data
          </div>
        ) : null,
    }),
    [isResponseEnd, sourceFlights.length],
  );

  const shouldShowLoadingState =
    isLoading || (!error && !isResponseEnd && sourceFlights.length === 0);

  useBeforeUnload(!isResponseEnd && !error);

  return (
    <div className="md:pt-2" id="flight-results">
      {/* Filters */}
      <div className="hidden md:block ">
        <CheapAirlineFilter />
      </div>
      {/* Flight results */}
      <div className="relative">
        <div ref={resultsRootRef} className="h-0" />
        {shouldShowLoadingState ? (
          <div className="mt-4 space-y-4 px-1 sm:px-4 pb-6">
            {LOADING_SKELETONS.map((_, i) => (
              <div key={i}>
                <FlightCardLoadingSkeleton />
              </div>
            ))}
          </div>
        ) : error && sourceFlights.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <NoFlightsFound mode="error" />
          </div>
        ) : sourceFlights.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <NoFlightsFound />
          </div>
        ) : (
          <Virtuoso
            useWindowScroll
            data={sourceFlights}
            overscan={200}
            rangeChanged={handleRangeChange}
            itemContent={renderFlight}
            computeItemKey={(_, flight) => flight.flight_id}
            components={virtuosoComponents}
          />
        )}
        {showBackToTop && (
          <button
            type="button"
            aria-label="Back to top"
            onClick={scrollToResultsAnchor}
            className="fixed bottom-6 right-6 z-40 rounded-full bg-primary text-primary-foreground shadow-lg p-2 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <ArrowUp className="h-5 w-5" />
          </button>
        )}
      </div>
      {selectedFlights.length > 0 && (
        <FlightBulkActionsToolbar
          selectFlight={{
            expireTime: selectedFlight.expireTime,
            data: selectedFlights,
          }}
          setShowOnlySelected={setShowOnlySelected}
          showOnlySelected={shouldShowOnlySelected}
        />
      )}
    </div>
  );
}
